import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../components/base/PageLayout';

const WisdomHospitality: React.FC = () => {
  useEffect(() => {
    document.title = 'おもてなしの心 - 執事が教える真のホスピタリティと心配りの技術｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが伝授する「おもてなしの心」。来客対応、VIPサービス、贈り物のエチケット、季節のもてなしなど、相手の心に寄り添う真のホスピタリティを解説。英国執事文化と日本のおもてなし精神を融合させた、プロフェッショナル執事ならではの心配りと気遣いの技術。接客業の方からホームパーティーを楽しみたい方まで、幅広く活用できます。');
    }
  }, []);

  const articles = [
    {
      title: 'VIP対応の極意',
      excerpt: '特別なお客様への最高級のサービスを提供するための心構えと技術を詳しく解説します。',
      path: '/wisdom/articles/vip-service',
      readTime: '8分',
      category: 'VIP対応',
      image: '/images/generated/seq-vip-service-card-unique-1-ffbf22.jpg'
    },
    {
      title: 'おもてなしの心構え',
      excerpt: '真のホスピタリティとは何か。お客様の心に響くサービスの本質を探ります。',
      path: '/wisdom/articles/mindset',
      readTime: '6分',
      category: 'ホスピタリティ',
      image: '/images/generated/seq-mindset-card-unique-2-ba1c00.jpg'
    },
    {
      title: 'コミュニケーション術',
      excerpt: 'お客様との信頼関係を築くための効果的なコミュニケーション技術をご紹介。',
      path: '/wisdom/articles/communication',
      readTime: '7分',
      category: 'コミュニケーション',
      image: '/images/generated/seq-communication-card-unique-3-506a4e.jpg'
    }
  ];

  const principles = [
    {
      number: '01',
      title: '察する力',
      description: 'お客様の言葉にならないニーズを感じ取り、先回りしてサービスを提供する',
      icon: 'ri-eye-line'
    },
    {
      number: '02',
      title: '細やかな配慮',
      description: '小さな変化にも気づき、常にお客様の快適さを最優先に考える',
      icon: 'ri-heart-line'
    },
    {
      number: '03',
      title: '誠実な姿勢',
      description: '偽りのない真摯な態度で、心からのサービスを提供する',
      icon: 'ri-shield-check-line'
    },
    {
      number: '04',
      title: '継続的な学び',
      description: '常に向上心を持ち、より良いサービスのために学び続ける',
      icon: 'ri-book-open-line'
    },
    {
      number: '05',
      title: '感謝の心',
      description: 'サービスを提供させていただく機会への感謝を忘れない',
      icon: 'ri-hand-heart-line'
    }
  ];

  return (
    <PageLayout hero={<>
<section className="relative py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{
              backgroundImage: `url('/images/gakken/gk-12.jpg')`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
          
          <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="max-w-6xl">
              <div className="inline-flex items-center px-3 md:px-5 sm:px-6 lg:px-8 py-2 bg-amber-700/20 backdrop-blur-sm rounded-full border border-amber-500/30 mb-6 md:mb-8">
                <i className="ri-heart-line text-amber-400 mr-2 text-sm md:text-base"></i>
                <span className="text-amber-200 text-xs md:text-sm font-medium">執事の知恵</span>
              </div>
              
              <h1 className="text-2xl md:text-6xl font-serif text-white mb-4 md:mb-6 leading-tight">
                おもてなしの心
                <span className="block text-base md:text-3xl text-amber-200 font-light mt-3 md:mt-4 italic">
                  The Art of True Hospitality
                </span>
              </h1>
              
              <p className="text-sm md:text-2xl text-gray-200 leading-relaxed max-w-6xl">
                真のホスピタリティとは、相手の心に寄り添い、期待を超える感動を提供することです。
                執事として培った「おもてなしの心」の本質をお伝えいたします。
              </p>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Introduction */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center px-3 py-1 bg-amber-100 rounded-full mb-6">
                  <span className="text-amber-800 text-sm font-medium">おもてなしの本質</span>
                </div>
                
                <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-8 leading-tight">
                  心と心の交流から生まれる
                  <span className="text-amber-700">真のサービス</span>
                </h2>
                
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p className="text-sm md:text-lg">
                    おもてなしとは、単なるサービスを超えた、心と心の交流です。相手の立場に立ち、その方が何を求めているかを察し、期待を上回る体験を提供することが真のおもてなしと言えるでしょう。
                  </p>
                  <p className="text-sm md:text-base">
                    執事文化の研究と実践を通じて学んだのは、技術的なスキルも重要ですが、それ以上に「相手を思いやる心」こそが最も大切だということです。この心があってこそ、真に価値のあるサービスが生まれるのです。
                  </p>
                  <p className="text-sm md:text-base">
                    ここでは、執事文化を探求する中で学んだおもてなしの心構えと実践的な技術を、具体的な事例とともにお伝えしていきます。
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg opacity-20 blur-lg"></div>
                <img 
                  src="/images/generated/a7d400fd387b9a348bc67357636c28f9.png"
                  alt="おもてなしの心を体現する執事"
                  className="relative w-full h-96 object-cover rounded-lg shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-lg shadow-xl border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <i className="ri-book-open-line text-amber-700 text-xl"></i>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">探求</div>
                      <div className="text-sm text-gray-600">日々学び続ける</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Principles */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-3 py-1 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-800 text-sm font-medium">5つの原則</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-6">
                おもてなしの基本原則
              </h2>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
                真のホスピタリティを実現するための、執事として大切にしている5つの原則をご紹介いたします
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {principles.map((principle, index) => (
                <div key={index} className="group relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 to-amber-600 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur"></div>
                  <div className="relative bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-4">
                        <i className={`${principle.icon} text-amber-700 text-xl`}></i>
                      </div>
                      <div className="text-2xl md:text-3xl font-serif text-amber-700 font-bold">
                        {principle.number}
                      </div>
                    </div>
                    <h3 className="text-lg md:text-xl font-serif text-gray-900 mb-4 group-hover:text-amber-700 transition-colors">
                      {principle.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      {principle.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Section */}
        <section className="py-20 bg-gradient-to-r from-amber-50 to-yellow-50">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <div className="relative">
              <i className="ri-double-quotes-l text-4xl md:text-6xl text-amber-300 absolute -top-4 -left-4"></i>
              <blockquote className="text-lg md:text-3xl font-serif text-gray-800 leading-relaxed mb-8 relative z-10">
                おもてなしとは、相手の幸せを願う心から生まれる、
                <span className="text-amber-700">最も美しい人間の表現</span>です
              </blockquote>
              <cite className="text-base md:text-lg text-gray-600 font-medium">
                執事ベル
              </cite>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-3 py-1 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-800 text-sm font-medium">関連記事</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-6">
                おもてなしを深く学ぶ
              </h2>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
                おもてなしの心をより深く理解し、実践するための記事をご用意いたしました
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <Link
                  key={index}
                  to={article.path}
                  className="group block bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="relative h-48 bg-gradient-to-br from-amber-100 to-amber-200">
                    <img 
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-amber-700">
                        {article.category}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-black/20 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg md:text-xl font-serif text-gray-900 mb-3 group-hover:text-amber-700 transition-colors leading-tight">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-amber-700 group-hover:text-amber-800 transition-colors">
                        <span className="text-sm font-medium">記事を読む</span>
                        <i className="ri-arrow-right-line ml-2 w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent"></div>
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage: `url('/images/generated/seq-cta-bg-7e4fcc.jpg')`
            }}
          />
          
          <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center px-5 sm:px-6 lg:px-8 py-2 bg-amber-700/20 backdrop-blur-sm rounded-full border border-amber-500/30 mb-8">
              <i className="ri-graduation-cap-line text-amber-400 mr-2"></i>
              <span className="text-amber-200 text-sm font-medium">学びの機会</span>
            </div>
            
            <h2 className="text-2xl md:text-4xl font-serif text-white mb-6 leading-tight">
              おもてなしの技術を
              <span className="text-amber-300">身につけませんか</span>
            </h2>
            
            <p className="text-base md:text-xl text-gray-200 mb-10 leading-relaxed max-w-2xl mx-auto">
              実践的な研修やコンサルティングを通じて、真のホスピタリティを身につけていただけます。
              一人ひとりに合わせたプログラムをご提供いたします。
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 bg-amber-700 text-white rounded-lg font-medium hover:bg-amber-800 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                <i className="ri-service-line mr-2"></i>
                サービス詳細を見る
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white border-2 border-white rounded-lg font-medium hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line mr-2"></i>
                お問い合わせ
              </Link>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default WisdomHospitality;